

import React, { Component } from 'react';

import memoizeOne from 'memoize-one';
import isDeepEqual from 'lodash.isequal';
import _get from 'lodash.get';
import { Loader } from '../../../ui-kit';
import AddonsContext, { defaults } from '../context/AddonsContext';
import Error from '../../Error';
import {
  getAddonsCost as getAddonsCostUtil,
  getCostForAddon,
  getCurrencyIcons as getCurrencyIconUtil,
  getCurrencySymbol as getCurrencySymbolUtil,
  getAddonValue,
  getAddonLabel,
  getFormattedAddon as getFormattedAddonUtil,
  getCaptions as getCaptionsUtil,
  mergeAddon,
  getAddonsCostByType as getAddonsCostByTypeUtil,
  getSupersizeMyEdits as getSupersizeMyEditsUtil,
  getAdditionalOutputs as getAdditionalOutputsUtil,
  formatAddons } from
'../utils';
import getConstants from '../constants';






















function isEqual(
[newBillingEntityCode, newIncludedAddons, newAddonsCosts],
[prevBillingEntityCode, prevIncludedAddons, prevAddonsCosts])
{
  return newBillingEntityCode === prevBillingEntityCode &&
  newAddonsCosts === prevAddonsCosts &&
  isDeepEqual(newIncludedAddons, prevIncludedAddons);
}

class AddonsProvider extends Component {
  static defaultProps = {
    loaderProps: {},
    showLoader: true
  };

  constructor(props) {
    super(props);

    const { billingEntityCode, includedAddons: includedAddonTypes, customAddonCosts } = props;

    this.state = {
      addonsCosts: null,
      billingEntityCode,
      includedAddons: includedAddonTypes || [],
      customAddonCosts: customAddonCosts || []
    };
  }

  componentDidMount() {
    this.fetchOrganisationData();
    this.fetchAddons();
  }

  componentDidUpdate(prevProps) {
    const { videoId } = this.props;

    if (videoId && prevProps.videoId !== videoId) {
      this.fetchOrganisationData();
    }
  }

  getProviderValue = memoizeOne(
    (billingEntityCode, includedAddons, addonsCosts, customAddonCosts) => {
      const {
        ADDITIONAL_OUTPUT: additionalOutput,
        DURATIONS: durations,
        RUSH_EDIT: rushEdit,
        CHROMA_KEY: chromaKey,
        ASPECT_RATIOS,
        CAPTIONS: captions,
        CAPTION_SAMPLES,
        VOICE_OVER_ARTIST: voiceOverArtist,
        SUPERSIZE_MY_EDIT: supersizeMyEdit
      } = getConstants();

      if (!addonsCosts) {
        // Conform to the context contract while costs are still loading.
        // With `renderChildWhenLoading`, children (e.g. the always-mounted
        // PaymentsInvoice consumer) render before this resolves; they
        // destructure `actions`, so it must always be present. `defaults`
        // supplies the full actions/constants shape with undefined values.
        return { ...defaults, state: { loading: true } };
      }
      const addonsProps = { billingEntityCode, includedAddons, addonsCosts, customAddonCosts };
      const ADDITIONAL_OUTPUT = additionalOutput(addonsProps);
      const CHROMA_KEY = chromaKey(addonsProps);
      const DURATIONS = durations(addonsProps);
      const RUSH_EDIT = rushEdit(addonsProps);
      const CAPTIONS = captions(addonsProps);
      const VOICE_OVER_ARTIST = voiceOverArtist(addonsProps);
      const SUPERSIZE_MY_EDIT = supersizeMyEdit(addonsProps);

      const getAddonsCost = getAddonsCostUtil(addonsProps);
      const getFormattedAddon = getFormattedAddonUtil({
        ADDITIONAL_OUTPUT,
        CHROMA_KEY,
        DURATIONS,
        VOICE_OVER_ARTIST,
        RUSH_EDIT
      });
      const getCaptions = getCaptionsUtil(CAPTIONS);
      const getCurrencySymbol = getCurrencySymbolUtil(billingEntityCode);
      const getCurrencyIcon = getCurrencyIconUtil(billingEntityCode);
      const getAddonsCostByType = getAddonsCostByTypeUtil(addonsCosts, billingEntityCode);
      const getSupersizeMyEdits = getSupersizeMyEditsUtil();
      const getAdditionalOutputs = getAdditionalOutputsUtil();
      const getBillingEntityCode = () => billingEntityCode;

      return {
        actions: {
          getAddonsCost,
          getCostForAddon,
          getAddonValue,
          getAddonLabel,
          getFormattedAddon,
          getCaptions,
          mergeAddon,
          getCurrencySymbol,
          getCurrencyIcon,
          getAddonsCostByType,
          getSupersizeMyEdits,
          getAdditionalOutputs,
          getBillingEntityCode,
          formatAddons
        },
        constants: {
          ADDITIONAL_OUTPUT,
          ASPECT_RATIOS,
          DURATIONS,
          RUSH_EDIT,
          CAPTIONS,
          CAPTION_SAMPLES,
          CHROMA_KEY,
          VOICE_OVER_ARTIST,
          SUPERSIZE_MY_EDIT,
          ADDONS_COST: addonsCosts
        },
        state: {
          loading: false
        }
      };
    }, isEqual);

  fetchAddons = async () => {
    const { getAddonsByType } = this.props;
    const { error, data } = await getAddonsByType();

    if (error || !data) {return void this.setState({ error: true });}

    this.setState({ addonsCosts: data.getAddonsByType });
  };

  fetchOrganisationData = async () => {
    const { videoId } = this.props;

    if (!videoId) {return;}

    const { getVideo } = this.props;
    const { error, data } = await getVideo(videoId);

    if (error || !data) {return void this.setState({ error: true });}

    this.setState({
      billingEntityCode: _get(data, 'getVideo.organisation.billingEntityCode'),
      includedAddons: _get(data, 'getVideo.organisation.package.includedAddonTypes') || []
    });
  };

  render() {
    const {
      addonsCosts,
      billingEntityCode,
      includedAddons,
      customAddonCosts,
      error
    } = this.state;
    const {
      children,
      loaderProps,
      showLoader,
      renderChildWhenLoading
    } = this.props;

    if (error) {return <Error />;}

    if (!addonsCosts && !renderChildWhenLoading) {
      return showLoader ? <Loader {...loaderProps} /> : null;
    }

    return (
      <AddonsContext.Provider
        value={this.getProviderValue(
          billingEntityCode, includedAddons, addonsCosts, customAddonCosts
        )}>

        {children}
      </AddonsContext.Provider>);

  }
}

export default AddonsProvider;